import { Button, Card, Input, message, Popconfirm, Select, Spin, Table } from 'antd';
import { DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getUserDetails, makeGetCall, makePostCall } from '../../utils/helper';
import './user-list.css';
import EditUser from './EditUser';
import DateFormatter from '../../components/DateFormatter';
import ErrorBoundary from '../../components/ErrorBoundary';

const { Search } = Input;
const { Option } = Select;

const UserList = () => {
  const [unitsList, setUnitsList] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [usersList, setUsersList] = useState({ data: [], loading: false, error: null });
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    units: false,
    permissions: false,
    roles: false,
    error: null
  });

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Memoized units mapping for performance
  const unitsMapping = useMemo(() => {
    return unitsList.reduce((acc, unit) => {
      acc[unit.unit_code] = unit.unit_label;
      return acc;
    }, {});
  }, [unitsList]);

  // Memoized filtered users for performance
  const filteredUsers = useMemo(() => {
    let filtered = usersList.data;

    // Filter by search text (name, username, email)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(user => {
        const fullName = user.first_name
          ? `${user.first_name} ${user.last_name || ''}`.toLowerCase()
          : (user.user_fullname || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        const email = (user.user_email || '').toLowerCase();

        return fullName.includes(searchLower) ||
               username.includes(searchLower) ||
               email.includes(searchLower);
      });
    }

    // Filter by role
    if (selectedRole) {
      filtered = filtered.filter(user => user.user_type === selectedRole);
    }

    // Filter by unit
    if (selectedUnit) {
      filtered = filtered.filter(user => user.user_unit_name === selectedUnit);
    }

    // Filter by status
    if (selectedStatus) {
      const isActive = selectedStatus === 'active';
      filtered = filtered.filter(user => {
        return user.is_active === (isActive ? 'Y' : 'N') ||
               user.active === isActive ||
               (selectedStatus === 'all');
      });
    }

    return filtered;
  }, [usersList.data, searchText, selectedRole, selectedUnit, selectedStatus]);

  const getUsersList = useCallback(() => {
    setUsersList({ loading: true, data: [], error: null });
    makePostCall('/user-list', {})
      .then(res => {
        setUsersList({ data: res.data?.data || [], loading: false, error: null });
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load users';
        setUsersList({ data: [], loading: false, error: errorMessage });
        message.error(`Error loading users: ${errorMessage}`);
      });
  }, []);

  const getUnitsList = useCallback(() => {
    setLoadingStates(prev => ({ ...prev, units: true, error: null }));
    makeGetCall('/center-options')
      .then(res => {
        const units = res.data?.data || [];
        setUnitsList(units);
        setLoadingStates(prev => ({ ...prev, units: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load units';
        setLoadingStates(prev => ({ ...prev, units: false, error: errorMessage }));
        message.error(`Error loading units: ${errorMessage}`);
      });
  }, []);

  const getPermissionsList = useCallback(() => {
    setLoadingStates(prev => ({ ...prev, permissions: true, error: null }));
    makePostCall('/permissions-list')
      .then(res => {
        setPermissionsList(res.data?.data || []);
        setLoadingStates(prev => ({ ...prev, permissions: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load permissions';
        setLoadingStates(prev => ({ ...prev, permissions: false, error: errorMessage }));
        message.error(`Error loading permissions: ${errorMessage}`);
      });
  }, []);

  const getRolesList = useCallback(() => {
    setLoadingStates(prev => ({ ...prev, roles: true, error: null }));
    makePostCall('/roles-list')
      .then(res => {
        setRolesList(res.data?.data || []);
        setLoadingStates(prev => ({ ...prev, roles: false }));
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load roles';
        setLoadingStates(prev => ({ ...prev, roles: false, error: errorMessage }));
        message.error(`Error loading roles: ${errorMessage}`);
      });
  }, []);

  // Add useEffect with proper dependencies
  useEffect(() => {
    getUnitsList();
    getPermissionsList();
    getRolesList();
    getUsersList();
  }, [getUnitsList, getPermissionsList, getRolesList, getUsersList]);

  const editUser = useCallback((user) => {
    setEditModalVisible(true);

    const rawModalities = user?.user_modalities?.um_list || "";
    console.log('Raw modalities from API:', rawModalities);

    const normalizedModalities = rawModalities?.split(',');
    setSelectedUser({
      ...user,
      user_modalities: normalizedModalities.length ? normalizedModalities : [],
    });
  }, []);

  const handleUserEditSubmit = useCallback((updatedData) => {
    const existingUserModalities = Array.isArray(selectedUser?.user_modalities)
      ? selectedUser.user_modalities.map((value) => `${value}`.trim()).filter(Boolean)
      : typeof selectedUser?.um_list === 'string'
        ? selectedUser.um_list.split(',').map((value) => value.trim()).filter(Boolean)
        : [];

    const normalizedModalities = Array.isArray(updatedData.user_modalities)
      ? updatedData.user_modalities.map((value) => `${value}`.trim()).filter(Boolean)
      : typeof updatedData.user_modalities === 'string'
        ? updatedData.user_modalities.split(',').map((value) => value.trim()).filter(Boolean)
        : typeof updatedData.um_list === 'string'
          ? updatedData.um_list.split(',').map((value) => value.trim()).filter(Boolean)
          : existingUserModalities;

    const { user_modalities, ...dataWithoutUserModalities } = updatedData;

    // Sanitize input data
    const sanitizedData = {
      ...dataWithoutUserModalities,
      user_firstname: updatedData.user_firstname?.trim(),
      user_lastname: updatedData.user_lastname?.trim(),
      user_email: updatedData.user_email?.trim()?.toLowerCase(),
      user_mobile: updatedData.user_mobile?.trim(),
      user_designation: updatedData.user_designation?.trim(),
      user_degree: updatedData.user_degree?.trim(),
      um_list: normalizedModalities.join(','),
      user_id: getUserDetails().username,
    };

    makePostCall('/add-user', sanitizedData)
      .then(res => {
        if (res.data?.success) {
          message.success('User updated successfully');
          setEditModalVisible(false);
          getUsersList();
        } else {
          const errorMessage = res.data?.message || 'Failed to update user';
          message.error(errorMessage);
        }
      })
      .catch(err => {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to update user';
        message.error(errorMessage);
      });
  }, [getUsersList, selectedUser]);

  // Status management functions - moved before USER_COLUMNS to fix hoisting issue
  const toggleUserStatus = useCallback((user) => {
    const newStatus = user.is_active === 'Y' ? 'N' : 'Y';
    const action = newStatus === 'Y' ? 'activate' : 'deactivate';

    setDeleteInProgress(true);
    makePostCall('/deactivate-user', {
      username: user.username,
      status: newStatus,
    })
      .then(res => {
        if (res.data?.success) {
          message.success(`User ${action}d successfully`);
          getUsersList();
        } else {
          const errorMessage = res.data?.message || `Failed to ${action} user`;
          message.error(errorMessage);
        }
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || `Failed to ${action} user`;
        message.error(errorMessage);
      })
      .finally(() => {
        setDeleteInProgress(false);
      });
  }, [getUsersList]);

  // Memoized table columns for performance
  const USER_COLUMNS = useMemo(() => [
    {
      title: 'Full Name',
      key: 'fullname',
      render: (_, record) => {
        return record.first_name
          ? `${record.first_name} ${record.last_name || ''}`
          : record.user_fullname;
      },
    },
    {
      title: 'Login',
      key: 'login',
      render: (_, record) => `${record.username}`,
    },
    {
      title: 'Profile',
      key: 'profile',
      render: (_, record) => `${record.user_type || 'NA'}`,
    },
    {
      title: 'Site Name',
      key: 'sitename',
      render: (_, record) => unitsMapping[record.user_unit_name] || 'NA',
    },
    {
      title: 'Created Date',
      key: 'created_dt',
      render: (_, record) => <DateFormatter date={record.user_created_dt} />,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <span className={record.is_active === 'Y' ? 'status-active' : 'status-inactive'}>
          {record.is_active === 'Y' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <div className="action-buttons">
          <Button
            onClick={() => editUser(record)}
            style={{ marginRight: '3px' }}
            title="Edit User"
          >
            <EditOutlined />
          </Button>
          <Popconfirm
            title={`${record.is_active === 'Y' ? 'Deactivate' : 'Activate'} user?`}
            description={`This will ${record.is_active === 'Y' ? 'deactivate' : 'activate'} the user account.`}
            onConfirm={() => toggleUserStatus(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              title={`${record.is_active === 'Y' ? 'Deactivate' : 'Activate'} User`}
              type={record.is_active === 'Y' ? 'default' : 'primary'}
            >
              {record.is_active === 'Y' ? <DeleteOutlined /> : 'Activate'}
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ], [unitsMapping, editUser, toggleUserStatus]);

  const isLoading = usersList.loading || deleteInProgress || loadingStates.units || loadingStates.permissions || loadingStates.roles;
  const loadingMessage = deleteInProgress ? "Processing user status..." : usersList.loading ? "Loading users..." : "Loading data...";

  // Clear filters function
  const clearFilters = useCallback(() => {
    setSearchText('');
    setSelectedRole('');
    setSelectedUnit('');
    setSelectedStatus('');
  }, []);


  // Memoized retry handler to prevent unnecessary re-renders
  const handleRetry = useCallback(() => {
    getUsersList();
    getUnitsList();
    getPermissionsList();
    getRolesList();
  }, [getUsersList, getUnitsList, getPermissionsList, getRolesList]);

  return (
    <ErrorBoundary>
      <Spin spinning={isLoading} tip={loadingMessage}>
        <div className="users-container admin-container">
        {(usersList.error || loadingStates.error) && (
          <div className="error-container">
            <strong>Error:</strong> {usersList.error || loadingStates.error}
            <Button
              type="link"
              size="small"
              onClick={handleRetry}
              className="retry-button"
            >
              Retry
            </Button>
          </div>
        )}
        <div>
          <Card
            bordered={false}
            style={{ width: '100%', margin: '1rem auto' }}
            className="admin-card"
          >
            {/* Title and Filter Controls in same row */}
            <div className="user-header admin-header">
              <div className="page-title admin-page-title">USERS</div>
              <div className="user-filters-inline admin-filters-inline">
                <Search
                  placeholder="Search by name, username, or email"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300, marginRight: 16 }}
                  prefix={<SearchOutlined />}
                />

                <Select
                  placeholder="Filter by Role"
                  value={selectedRole}
                  onChange={setSelectedRole}
                  style={{ width: 150, marginRight: 16 }}
                  allowClear
                >
                  {rolesList.map(role => (
                    <Option key={role.role_code || role.value} value={role.role_code || role.value}>
                      {role.role_label || role.label}
                    </Option>
                  ))}
                </Select>

                <Select
                  placeholder="Filter by Unit"
                  value={selectedUnit}
                  onChange={setSelectedUnit}
                  style={{ width: 150, marginRight: 16 }}
                  allowClear
                >
                  {unitsList.map(unit => (
                    <Option key={unit.code || unit.value} value={unit.code || unit.value}>
                      {unit.name || unit.label}
                    </Option>
                  ))}
                </Select>

                <Select
                  placeholder="Filter by Status"
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  style={{ width: 150, marginRight: 16 }}
                  allowClear
                >
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>

                <Button onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </div>
            <Table
              className="user-list-table admin-table"
              columns={USER_COLUMNS}
              dataSource={filteredUsers}
              rowKey={(record) => record.username}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} users`,
                pageSizeOptions: ['10', '20', '50', '100'],
                showLessItems: true,
              }}
              locale={{
                emptyText: searchText || selectedRole || selectedUnit || selectedStatus
                  ? 'No users match the current filters'
                  : 'No users found'
              }}
            />
          </Card>
        </div>
      </div>

      <EditUser
        visible={editModalVisible}
        userData={selectedUser}
        unitsList={unitsList}
        rolesList={rolesList}
        permissionsList={permissionsList}
        onCancel={() => setEditModalVisible(false)}
        onSubmit={handleUserEditSubmit}
      />
      </Spin>
    </ErrorBoundary>
  );
};

export default UserList;
